// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Rich Text Editor (TipTap)
// Full-featured WYSIWYG editor for KB articles
// Features: headings, bold/italic/code, links, images, tables, code blocks,
//           task lists, highlights, undo/redo
// ═══════════════════════════════════════════════════════════════════════════════
import DOMPurify from 'dompurify';
import { useCallback, useState } from 'react';
import { useEditor, EditorContent, BubbleMenu, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { theme, fonts } from '../../lib/theme';

const lowlight = createLowlight(common);

// ─── Types ───
interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  editable?: boolean;
  minHeight?: number;
}

// ─── Main Editor Component ───
export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Start writing your article...',
  editable = true,
  minHeight = 400,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // replaced by CodeBlockLowlight
        heading: { levels: [1, 2, 3, 4] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { style: `color: ${theme.accent}; text-decoration: underline;` },
      }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      CodeBlockLowlight.configure({ lowlight }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style: [
          `min-height: ${minHeight}px`,
          `padding: 20px`,
          `color: ${theme.text}`,
          `font-family: ${fonts.primary}`,
          `font-size: 14px`,
          `line-height: 1.7`,
          `outline: none`,
        ].join(';'),
      },
    },
  });

  if (!editor) return null;

  return (
    <div style={{
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {editable && <EditorToolbar editor={editor} />}
      {editable && editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <BubbleToolbar editor={editor} />
        </BubbleMenu>
      )}
      <div style={{
        overflow: 'auto',
        maxHeight: 'calc(100vh - 300px)',
      }}>
        <EditorContent editor={editor} />
      </div>
      {editable && (
        <div style={{
          borderTop: `1px solid ${theme.border}`,
          padding: '6px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 10,
          color: theme.textTertiary,
        }}>
          <span>{editor.storage.characterCount?.characters?.() || 0} characters</span>
          <span>Markdown shortcuts supported</span>
        </div>
      )}
    </div>
  );
}

// ─── Editor Toolbar ───
function EditorToolbar({ editor }: { editor: Editor }) {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  const addLink = useCallback(() => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    const url = window.prompt('Image URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const addTable = useCallback(() => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  return (
    <div style={{
      borderBottom: `1px solid ${theme.border}`,
      padding: '4px 8px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'center',
      background: theme.input,
    }}>
      {/* Text style group */}
      <ToolbarGroup>
        <ToolbarSelect
          value={editor.isActive('heading', { level: 1 }) ? 'h1'
            : editor.isActive('heading', { level: 2 }) ? 'h2'
            : editor.isActive('heading', { level: 3 }) ? 'h3'
            : 'p'}
          onChange={(val) => {
            if (val === 'p') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: Number(val[1]) as 1 | 2 | 3 }).run();
          }}
          options={[
            { value: 'p', label: 'Paragraph' },
            { value: 'h1', label: 'Heading 1' },
            { value: 'h2', label: 'Heading 2' },
            { value: 'h3', label: 'Heading 3' },
          ]}
        />
      </ToolbarGroup>

      <ToolbarDivider />

      {/* Inline formatting */}
      <ToolbarGroup>
        <ToolbarBtn icon="B" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)" bold />
        <ToolbarBtn icon="I" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)" italic />
        <ToolbarBtn icon="S" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" strike />
        <ToolbarBtn icon="<>" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline Code" mono />
        <ToolbarBtn icon="🖍️" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight" />
      </ToolbarGroup>

      <ToolbarDivider />

      {/* Block elements */}
      <ToolbarGroup>
        <ToolbarBtn icon="•" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List" />
        <ToolbarBtn icon="1." active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List" />
        <ToolbarBtn icon="☑" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} title="Task List" />
        <ToolbarBtn icon="❝" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote" />
        <ToolbarBtn icon="{ }" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code Block" />
        <ToolbarBtn icon="—" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule" />
      </ToolbarGroup>

      <ToolbarDivider />

      {/* Insert */}
      <ToolbarGroup>
        {showLinkInput ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addLink()}
              placeholder="https://..."
              autoFocus
              style={{
                background: theme.bg2, border: `1px solid ${theme.border}`, borderRadius: 4,
                padding: '2px 6px', fontSize: 11, color: theme.text, fontFamily: 'inherit', width: 160,
              }}
            />
            <ToolbarBtn icon="✓" onClick={addLink} title="Apply Link" />
            <ToolbarBtn icon="✕" onClick={() => { setShowLinkInput(false); setLinkUrl(''); }} title="Cancel" />
          </div>
        ) : (
          <ToolbarBtn icon="🔗" active={editor.isActive('link')} onClick={() => {
            if (editor.isActive('link')) editor.chain().focus().unsetLink().run();
            else setShowLinkInput(true);
          }} title="Link" />
        )}
        <ToolbarBtn icon="🖼" onClick={addImage} title="Insert Image" />
        <ToolbarBtn icon="▦" onClick={addTable} title="Insert Table" />
      </ToolbarGroup>

      <div style={{ flex: 1 }} />

      {/* Undo/Redo */}
      <ToolbarGroup>
        <ToolbarBtn icon="↩" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)" />
        <ToolbarBtn icon="↪" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Shift+Z)" />
      </ToolbarGroup>
    </div>
  );
}

// ─── Bubble Menu (appears on text selection) ───
function BubbleToolbar({ editor }: { editor: Editor }) {
  return (
    <div style={{
      display: 'flex',
      gap: 2,
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: 8,
      padding: '3px 6px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    }}>
      <ToolbarBtn icon="B" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} bold />
      <ToolbarBtn icon="I" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} italic />
      <ToolbarBtn icon="<>" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} mono />
      <ToolbarBtn icon="🔗" active={editor.isActive('link')} onClick={() => {
        if (editor.isActive('link')) {
          editor.chain().focus().unsetLink().run();
        } else {
          const url = window.prompt('URL:');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }
      }} />
      <ToolbarBtn icon="🖍️" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} />
    </div>
  );
}

// ─── Toolbar Primitives ───
interface ToolbarBtnProps {
  icon: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  mono?: boolean;
}

function ToolbarBtn({ icon, onClick, active, disabled, title, bold, italic, strike, mono }: ToolbarBtnProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: bold ? 700 : 400,
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: strike ? 'line-through' : 'none',
        fontFamily: mono ? fonts.mono : 'inherit',
        borderRadius: 4,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? theme.accentGlow : 'transparent',
        color: active ? theme.accent : disabled ? theme.textTertiary : theme.textSecondary,
        transition: 'all .1s',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {icon}
    </button>
  );
}

function ToolbarGroup({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>{children}</div>;
}

function ToolbarDivider() {
  return <div style={{ width: 1, height: 20, background: theme.border, margin: '0 4px' }} />;
}

interface ToolbarSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

function ToolbarSelect({ value, onChange, options }: ToolbarSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: theme.bg2,
        border: `1px solid ${theme.border}`,
        borderRadius: 4,
        padding: '2px 6px',
        fontSize: 11,
        color: theme.text,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ─── Read-Only Renderer ───
// For displaying article content without editor overhead
export function ArticleRenderer({ content }: { content: string }) {
  return (
    <div
      className="article-content"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allowfullscreen', 'webkitallowfullscreen', 'mozallowfullscreen', 'frameborder', 'allow', 'src', 'width', 'height'],
        // Mammoth embeds DOCX images as <img src="data:image/png;base64,...">.
        // DOMPurify's default URI policy strips data: URLs, leaving broken
        // image placeholders. Whitelisting data: URIs for img/video lets the
        // embedded images render again.
        ADD_DATA_URI_TAGS: ['img', 'video', 'audio'],
      }) }}
      style={{
        color: theme.text,
        fontFamily: fonts.primary,
        fontSize: 14,
        lineHeight: 1.7,
      }}
    />
  );
}

// ─── Shared content styles (used by both editor and read-only renderer) ───
const contentStyles = (selector: string) => `
  ${selector} h1 { font-size: 24px; font-weight: 800; margin: 24px 0 12px; color: ${theme.text}; line-height: 1.3; }
  ${selector} h2 { font-size: 20px; font-weight: 700; margin: 20px 0 10px; color: ${theme.text}; line-height: 1.35; border-bottom: 1px solid ${theme.border}; padding-bottom: 6px; }
  ${selector} h3 { font-size: 16px; font-weight: 600; margin: 16px 0 8px; color: ${theme.text}; line-height: 1.4; }
  ${selector} h4 { font-size: 14px; font-weight: 600; margin: 14px 0 6px; color: ${theme.textSecondary}; text-transform: uppercase; letter-spacing: 0.3px; }
  ${selector} p { margin: 8px 0; }
  ${selector} ul, ${selector} ol { padding-left: 24px; margin: 8px 0; }
  ${selector} li { margin: 4px 0; }
  ${selector} li > ul, ${selector} li > ol { margin: 2px 0; }
  ${selector} blockquote {
    border-left: 3px solid ${theme.accent};
    padding-left: 16px;
    margin: 12px 0;
    color: ${theme.textSecondary};
    font-style: italic;
  }
  ${selector} code {
    background: ${theme.input};
    border-radius: 4px;
    padding: 2px 5px;
    font-family: ${fonts.mono};
    font-size: 13px;
    color: ${theme.accent};
  }
  ${selector} pre {
    background: ${theme.bg};
    border: 1px solid ${theme.border};
    border-radius: 8px;
    padding: 16px;
    overflow-x: auto;
    margin: 12px 0;
  }
  ${selector} pre code {
    background: none;
    padding: 0;
    color: ${theme.text};
    font-size: 12px;
    line-height: 1.5;
  }
  ${selector} img {
    max-width: 100%;
    border-radius: 8px;
    margin: 12px 0;
  }
  ${selector} table {
    border-collapse: collapse;
    width: 100%;
    margin: 12px 0;
  }
  ${selector} th, ${selector} td {
    border: 1px solid ${theme.border};
    padding: 8px 12px;
    text-align: left;
    font-size: 13px;
  }
  ${selector} th {
    background: ${theme.input};
    font-weight: 600;
  }
  ${selector} tr:nth-child(even) td {
    background: ${theme.input}33;
  }
  ${selector} hr {
    border: none;
    border-top: 1px solid ${theme.border};
    margin: 20px 0;
  }
  ${selector} mark {
    background: rgba(245, 158, 11, 0.3);
    border-radius: 2px;
    padding: 1px 2px;
  }
  ${selector} ul[data-type="taskList"] {
    list-style: none;
    padding-left: 4px;
  }
  ${selector} ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  ${selector} ul[data-type="taskList"] li input[type="checkbox"] {
    margin-top: 5px;
    accent-color: ${theme.accent};
  }
  ${selector} a {
    color: ${theme.accent};
    text-decoration: underline;
  }
  ${selector} strong { font-weight: 700; }
  ${selector} .hljs-keyword { color: ${theme.mode === 'light' ? '#a626a4' : '#c678dd'}; }
  ${selector} .hljs-string { color: ${theme.mode === 'light' ? '#50a14f' : '#98c379'}; }
  ${selector} .hljs-comment { color: ${theme.mode === 'light' ? '#8e8f97' : '#5c6370'}; font-style: italic; }
  ${selector} .hljs-function { color: ${theme.mode === 'light' ? '#4078f2' : '#61afef'}; }
  ${selector} .hljs-number { color: ${theme.mode === 'light' ? '#986801' : '#d19a66'}; }
  ${selector} .hljs-title { color: ${theme.mode === 'light' ? '#c18401' : '#e5c07b'}; }
`;

// ─── Editor Styles (inject as <style> tag) ───
// Functions (not constants) so the CSS is rebuilt from the live theme after
// a light/dark switch — a module-level string would freeze dark-mode colors.
export const editorStyles = () => `
  .ProseMirror {
    outline: none;
  }
  ${contentStyles('.ProseMirror')}
  .ProseMirror p.is-editor-empty:first-child::before {
    color: ${theme.textTertiary};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
`;

// ─── Article Renderer Styles (inject as <style> tag for read-only KB view) ───
export const articleContentStyles = () => `
  .article-content {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  ${contentStyles('.article-content')}
  .article-content > *:first-child { margin-top: 0; }
  .article-content > *:last-child { margin-bottom: 0; }
  .article-content iframe {
    max-width: 100%;
    border-radius: 8px;
    margin: 12px 0;
  }
  .article-content figure {
    margin: 16px 0;
  }
  .article-content figure figcaption {
    font-size: 12px;
    color: ${theme.textSecondary};
    text-align: center;
    margin-top: 6px;
  }
  .article-content dl {
    margin: 8px 0;
  }
  .article-content dt {
    font-weight: 600;
    margin-top: 8px;
  }
  .article-content dd {
    margin-left: 16px;
    color: ${theme.textSecondary};
  }
`;
